<template>
  <el-dialog :title="dialogTitle" :visible.sync="visible" width="720px" :before-close="handleClose"
    :close-on-click-modal="false">
    <div v-loading="loading">
      <!-- 查看模式 -->
      <div v-show="mode === 'view'">
        <div class="task-head">
          <span class="task-title">{{ task.title }}</span>
          <el-tag :type="stateTagType(task.state)" size="small">{{ getTaskStateName(task.state) }}</el-tag>
        </div>
        <div class="reward-line">报酬：<span class="reward-text">￥{{ task.reward }}</span></div>

        <div class="info-grid">
          <div class="info-item"><label>发布人：</label><span>{{ task.publishUserName || '-' }}</span></div>
          <div class="info-item"><label>接单人：</label><span>{{ task.acceptUserName || '-' }}</span></div>
          <div class="info-item"><label>期望完成：</label><span>{{ task.taskTime || '-' }}</span></div>
          <div class="info-item"><label>地址：</label><span>{{ task.address || '-' }}</span></div>
          <div class="info-item"><label>联系人：</label><span>{{ task.contactName || '-' }}</span></div>
          <div class="info-item"><label>联系方式：</label><span>{{ task.contactTel || '-' }}</span></div>
          <div class="info-item"><label>发布时间：</label><span>{{ task.createTime || '-' }}</span></div>
          <div class="info-item"><label>接单时间：</label><span>{{ task.acceptTime || '-' }}</span></div>
          <div class="info-item"><label>提交时间：</label><span>{{ task.submitTime || '-' }}</span></div>
          <div class="info-item"><label>完成时间：</label><span>{{ task.finishTime || '-' }}</span></div>
        </div>

        <div class="block">
          <div class="block-title">任务描述</div>
          <div class="block-content">{{ task.content || '无' }}</div>
        </div>

        <div class="block" v-if="task.photos && task.photos.length">
          <div class="block-title">完成凭证</div>
          <div class="photo-list">
            <el-image v-for="(photo, index) in task.photos" :key="index" class="photo-item"
              :src="buildTaskPhotoUrl(photo.url)" :preview-src-list="photoPreviewList" fit="cover" />
          </div>
        </div>

        <div class="block" v-if="task.logs && task.logs.length">
          <div class="block-title">任务进度</div>
          <el-timeline>
            <el-timeline-item v-for="(log, index) in task.logs" :key="index" :timestamp="log.createTime"
              placement="top">
              <div class="log-item">
                <span class="log-action">{{ getTaskActionName(log.action) }}</span>
                <span v-if="log.amount && Number(log.amount) > 0" class="log-amount">￥{{ log.amount }}</span>
              </div>
              <div class="log-remark">{{ log.remark }}</div>
            </el-timeline-item>
          </el-timeline>
        </div>
      </div>

      <!-- 修改模式（发布者，待接单） -->
      <el-form v-show="mode === 'edit'" ref="editForm" :model="editForm" :rules="editRules" label-width="110px">
        <el-form-item label="任务标题" prop="title">
          <el-input v-model="editForm.title" maxlength="64" show-word-limit />
        </el-form-item>
        <el-form-item label="任务描述" prop="content">
          <el-input v-model="editForm.content" type="textarea" :rows="3" maxlength="1024" show-word-limit />
        </el-form-item>
        <el-form-item label="期望完成时间" prop="taskTime">
          <el-date-picker v-model="editForm.taskTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%" />
        </el-form-item>
        <el-form-item label="地址" prop="address">
          <el-input v-model="editForm.address" maxlength="200" />
        </el-form-item>
        <el-form-item label="联系人" prop="contactName">
          <el-input v-model="editForm.contactName" maxlength="64" />
        </el-form-item>
        <el-form-item label="联系方式" prop="contactTel">
          <el-input v-model="editForm.contactTel" maxlength="20" />
        </el-form-item>
        <el-form-item label="任务报酬">
          <span class="reward-text">￥{{ task.reward }}</span>
          <span class="tip-text">（报酬已托管，不可修改）</span>
        </el-form-item>
      </el-form>

      <!-- 提交完成模式（接单人，进行中） -->
      <div v-show="mode === 'submit'">
        <div class="block-title">上传完成凭证照片</div>
        <el-upload :action="uploadAction" list-type="picture-card" :file-list="submitFileList"
          :http-request="customUpload" :on-remove="handleRemovePhoto" :limit="6" accept="image/*">
          <i class="el-icon-plus"></i>
        </el-upload>
        <div class="tip-text">请上传能证明任务已完成的照片，最多6张，提交后等待发布者确认放款。</div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <!-- 查看模式按钮 -->
      <template v-if="mode === 'view'">
        <el-button v-if="canAccept" type="success" :loading="submitting" @click="handleAccept">接单</el-button>
        <el-button v-if="canEdit" type="primary" @click="openEdit">修改</el-button>
        <el-button v-if="canSubmit" type="primary" @click="openSubmit">提交完成</el-button>
        <el-button v-if="canConfirm" type="warning" :loading="submitting" @click="handleConfirm">确认完成</el-button>
        <el-button v-if="canCancel" type="danger" :loading="submitting" @click="handleCancel">取消任务</el-button>
        <el-button @click="handleClose">关闭</el-button>
      </template>
      <!-- 修改模式按钮 -->
      <template v-else-if="mode === 'edit'">
        <el-button @click="mode = 'view'">返回</el-button>
        <el-button type="primary" :loading="submitting" @click="handleUpdate">保存修改</el-button>
      </template>
      <!-- 提交模式按钮 -->
      <template v-else>
        <el-button @click="mode = 'view'">返回</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">确认提交</el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import {
  getTaskWallDetail,
  acceptTaskWall,
  submitTaskWall,
  confirmTaskWall,
  cancelTaskWall,
  updateTaskWall,
  getTaskStateName,
  getTaskActionName,
  buildTaskPhotoUrl,
  TASK_STATE
} from '@/api/community/taskWallManageApi'
import { uploadFile } from '@/api/staff/addStaffApi'
import { getUserId } from '@/api/user/userApi'

export default {
  name: 'ViewTaskWall',
  data() {
    return {
      visible: false,
      loading: false,
      submitting: false,
      mode: 'view',
      taskId: '',
      task: {},
      uploadAction: '#',
      submitFileList: [],
      submitPhotos: [],
      editForm: {
        title: '',
        content: '',
        taskTime: '',
        address: '',
        contactName: '',
        contactTel: ''
      },
      editRules: {
        title: [{ required: true, message: '请输入任务标题', trigger: 'blur' }],
        taskTime: [{ required: true, message: '请选择期望完成时间', trigger: 'change' }],
        address: [{ required: true, message: '请输入地址', trigger: 'blur' }],
        contactName: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
        contactTel: [{ required: true, message: '请输入联系方式', trigger: 'blur' }]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.mode === 'edit' ? '修改任务' : (this.mode === 'submit' ? '提交完成凭证' : '许愿墙任务详情')
    },
    photoPreviewList() {
      return (this.task.photos || []).map(photo => buildTaskPhotoUrl(photo.url))
    },
    isPublisher() {
      return this.task.publishUserId && this.task.publishUserId === getUserId()
    },
    isAccepter() {
      return this.task.acceptUserId && this.task.acceptUserId === getUserId()
    },
    canAccept() {
      return this.task.state === TASK_STATE.WAIT_ACCEPT && !this.isPublisher
    },
    canEdit() {
      return this.task.state === TASK_STATE.WAIT_ACCEPT && this.isPublisher
    },
    canCancel() {
      return this.task.state === TASK_STATE.WAIT_ACCEPT && this.isPublisher
    },
    canSubmit() {
      return this.task.state === TASK_STATE.DOING && this.isAccepter
    },
    canConfirm() {
      return (this.task.state === TASK_STATE.WAIT_CONFIRM || this.task.state === TASK_STATE.PLATFORM_AUDIT) &&
        this.isPublisher
    }
  },
  methods: {
    getTaskStateName,
    getTaskActionName,
    buildTaskPhotoUrl,
    open(taskId) {
      this.taskId = taskId
      this.mode = 'view'
      this.submitFileList = []
      this.submitPhotos = []
      this.visible = true
      this.fetchDetail()
    },
    async fetchDetail() {
      try {
        this.loading = true
        const res = await getTaskWallDetail(this.taskId)
        if (res.code !== 0) {
          this.$message.error(res.msg || '查询详情失败')
          return
        }
        this.task = res.data || {}
      } catch (error) {
        console.error('查询任务详情失败:', error)
        this.$message.error('查询任务详情失败')
      } finally {
        this.loading = false
      }
    },
    stateTagType(state) {
      const map = {
        '1000': 'info',
        '2000': 'warning',
        '3000': '',
        '4000': 'warning',
        '5000': 'danger',
        '6000': 'success',
        '7000': 'info'
      }
      return map[state] || 'info'
    },
    handleAccept() {
      this.$confirm('确认接下该任务？接单后请及时完成并提交凭证。', '提示', {
        confirmButtonText: '确认接单',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await this.doAction(() => acceptTaskWall({ taskId: this.taskId }), '接单成功')
      }).catch(() => {})
    },
    handleConfirm() {
      this.$confirm('确认任务已完成？确认后报酬将从托管账户发放给接单人。', '提示', {
        confirmButtonText: '确认完成',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await this.doAction(() => confirmTaskWall({ taskId: this.taskId }), '确认完成，报酬已发放')
      }).catch(() => {})
    },
    handleCancel() {
      this.$confirm('确认取消该任务？取消后托管报酬将退回您的钱包。', '提示', {
        confirmButtonText: '确认取消',
        cancelButtonText: '再想想',
        type: 'warning'
      }).then(async () => {
        await this.doAction(() => cancelTaskWall({ taskId: this.taskId }), '取消成功，报酬已退回钱包')
      }).catch(() => {})
    },
    openEdit() {
      this.editForm = {
        title: this.task.title || '',
        content: this.task.content || '',
        taskTime: this.task.taskTime || '',
        address: this.task.address || '',
        contactName: this.task.contactName || '',
        contactTel: this.task.contactTel || ''
      }
      this.mode = 'edit'
      this.$nextTick(() => {
        if (this.$refs.editForm) {
          this.$refs.editForm.clearValidate()
        }
      })
    },
    handleUpdate() {
      this.$refs.editForm.validate(async valid => {
        if (!valid) {
          return
        }
        await this.doAction(() => updateTaskWall({
          taskId: this.taskId,
          ...this.editForm
        }), '修改成功', () => {
          this.mode = 'view'
        })
      })
    },
    openSubmit() {
      this.submitFileList = []
      this.submitPhotos = []
      this.mode = 'submit'
    },
    // 自定义上传：走统一上传组件，收集 fileId
    async customUpload(option) {
      try {
        const formData = new FormData()
        formData.append('uploadFile', option.file)
        formData.append('communityId', '-1')
        const res = await uploadFile(formData)
        const fileId = res.fileId || res.url
        this.submitPhotos.push(fileId)
        option.onSuccess(res, option.file)
      } catch (error) {
        console.error('上传失败:', error)
        this.$message.error('上传失败')
        option.onError(error)
      }
    },
    handleRemovePhoto(file, fileList) {
      // 依据剩余文件重建 photo 列表，保证与展示一一对应
      this.submitPhotos = fileList
        .filter(item => item.response)
        .map(item => item.response.fileId || item.response.url)
    },
    async handleSubmit() {
      if (!this.submitPhotos.length) {
        this.$message.warning('请至少上传一张完成凭证照片')
        return
      }
      await this.doAction(() => submitTaskWall({
        taskId: this.taskId,
        photos: this.submitPhotos
      }), '提交成功，等待发布者确认', () => {
        this.mode = 'view'
      })
    },
    // 统一的操作执行封装：成功提示 + 刷新详情 + 通知列表 + 额外回调
    async doAction(requestFn, successMsg, after) {
      try {
        this.submitting = true
        const res = await requestFn()
        if (res.code === 0) {
          this.$message.success(res.msg || successMsg)
          if (after) {
            after()
          }
          this.$emit('success')
          await this.fetchDetail()
        } else {
          this.$message.error(res.msg || '操作失败')
        }
      } catch (error) {
        console.error('操作失败:', error)
        this.$message.error('操作失败')
      } finally {
        this.submitting = false
      }
    },
    handleClose() {
      this.visible = false
      this.mode = 'view'
      this.task = {}
    }
  }
}
</script>

<style lang="scss" scoped>
.task-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  .task-title {
    font-size: 18px;
    font-weight: bold;
  }
}

.reward-line {
  margin-bottom: 12px;
  font-size: 14px;
}

.reward-text {
  color: #f56c6c;
  font-weight: bold;
  font-size: 16px;
}

.info-grid {
  display: flex;
  flex-wrap: wrap;
  border-top: 1px solid #ebeef5;
  border-left: 1px solid #ebeef5;

  .info-item {
    width: 50%;
    box-sizing: border-box;
    padding: 8px 12px;
    border-right: 1px solid #ebeef5;
    border-bottom: 1px solid #ebeef5;
    font-size: 13px;

    label {
      color: #909399;
    }
  }
}

.block {
  margin-top: 16px;
}

.block-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #303133;
}

.block-content {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  white-space: pre-wrap;
}

.photo-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  .photo-item {
    width: 100px;
    height: 100px;
    border-radius: 4px;
  }
}

.log-item {
  display: flex;
  align-items: center;

  .log-action {
    font-weight: bold;
    color: #303133;
  }

  .log-amount {
    margin-left: 8px;
    color: #f56c6c;
  }
}

.log-remark {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.tip-text {
  color: #909399;
  font-size: 12px;
  margin-top: 8px;
}

.dialog-footer {
  text-align: right;
}
</style>
